import Form, {FormOnSubmit} from 'components/ui/Form/Form/Form';

import {
    ICrpBpListItem,
    useAddBusinessPartnerRequiredInformations,
    useGetBusinessPartnerRequiredDocuments,
    useGetBusinessPartnerRequiredInformations,
    useUploadBusinessPartnerDocument,
} from 'api/business-partner';
import FileUploadButton from 'components/ui/Form/FileUploadButton/FileUploadButton';
import useFileUploadStatuses, {
    isNewFileUploadStatus,
} from 'components/ui/Form/FileUploadButton/useFileUploadStatuses';
import {
    BusinessPartner,
    BusinessPartnerRequiredDocument,
    BusinessPartnerRequiredInformation,
    PaymentCustomer,
} from 'types/api';
import {useUploadCrpDocument} from 'api/crp';
import {translate} from 'helpers/localize';
import classNames from 'classnames';
import {REQUIRED_DOCUMENTS} from 'constants/cache';
import InfoPopover from 'components/ui/InfoPopover/InfoPopover';
import {LegalType} from 'types/onboarding';
import {SOURCE_OF_FUNDS} from 'constants/common';
import NextStepButton from '../components/NextStepButton';
import StepTitle from '../components/StepTitle';
import {IDefaultStepProps} from '../useOnboarding';

import styles from './Steps.module.scss';
import {useQueryClient} from '@tanstack/react-query';
import TextArea from 'components/ui/Form/TexArea/TextArea';
import {useForm} from 'react-hook-form';
import {handleTypedFormError} from 'helpers/handleFormError';
import IconSVG from 'components/ui/Icon/IconSVG';
import {useEffect} from 'react';

const getRequiredDocumentText = ({
    requiredDocument,
    legalType,
    paymentCustomer,
    businessPartner,
}: {
    requiredDocument: BusinessPartnerRequiredDocument;
    legalType: LegalType | null;
    paymentCustomer?: PaymentCustomer | null;
    businessPartner: BusinessPartner;
}): {label: string; info: React.ReactNode} => {
    const sourceOfFunds = legalType
        ? translate(
              SOURCE_OF_FUNDS[legalType]?.find(
                  s => s['@id'] === businessPartner.gwgInfo?.sourceOfFunds,
              )?.label,
          ) || businessPartner.gwgInfo?.sourceOfFunds
        : businessPartner.gwgInfo?.sourceOfFunds;

    const getRequiredDocumentLabel = (label: string) => {
        const name =
            requiredDocument.category === 'incoming_payment_customer' ||
            requiredDocument.category === 'outgoing_payment_customer'
                ? paymentCustomer?.name
                : requiredDocument.crpId
                  ? requiredDocument.crpFullName || ''
                  : '';

        if (!name) {
            return translate(label);
        }

        const labelFormat = label.endsWith(' for')
            ? `${label} %{name}`
            : `${label} (%{name})`;

        return translate(labelFormat, {name});
    };

    switch (requiredDocument.category.toLowerCase()) {
        case 'id_document':
            return {
                label: getRequiredDocumentLabel('ID document'),
                info: 'Document serving as proof of identity, including passport or national ID card.',
            };
        case 'utility_bill':
            return {
                label: getRequiredDocumentLabel('Utility bill for'),
                info: 'A recent electricity, water, or phone/internet bill, or alternatively, a tax or other official government bill, with the personal name and address visible (should not be older than 6 months).',
            };
        case 'company_org_chart':
            return {
                label: getRequiredDocumentLabel('Company Organization Chart'),
                info: 'Document providing insight into the structure of the company group, such as an organizational chart, details about parent companies, subsidiaries, and legal affiliations.',
            };
        case 'edd_evidence_inheritance':
            return {
                label: getRequiredDocumentLabel('Evidence of inheritance'),
                info: 'Inheritance certificates, wills, or legal documents related to inherited assets.',
            };
        case 'edd_evidence_employment':
            return {
                label: getRequiredDocumentLabel('Evidence of employment'),
                info: 'Examples of employment evidence include pay stubs, employment contracts, or a letter of employment.',
            };
        case 'incoming_payment_customer':
            return {
                label: getRequiredDocumentLabel('Invoice or contract for'),
                info: 'Bank statements or payment receipts showing payments received from customers, as well as contracts or agreements related to such payments.',
            };
        case 'outgoing_payment_customer':
            return {
                label: getRequiredDocumentLabel('Invoice or contract for'),
                info: 'Bank statements or payment receipts showing payments made to suppliers or other third parties, as well as contracts or agreements related to such payments.',
            };
        case 'proof_of_ownership':
            return {
                label: getRequiredDocumentLabel('Proof of company ownership'),
                info: 'A document serving as proof of ownership for the respective asset or property. Examples include business registration certificates, extracts from commercial registries or corporate share certificates.',
            };
        // case 'private_source_of_funds':
        //     return {
        //         label: getRequiredDocumentLabel('Proof of source of funds'),
        //         info: (
        //             <span>
        //                 Document providing proof of funds from &quot;
        //                 <var data-var="sourceOfFunds">{sourceOfFunds}</var>
        //                 &quot;. The address, amount and date/time stamp should
        //                 be visible on the document.
        //             </span>
        //         ),
        //     };
        case 'investment_evidence':
            return {
                label: getRequiredDocumentLabel('Evidence of investment'),
                info: 'Investment account statements, stock certificates, or other records of investment holdings.',
            };
        case 'resident_permit':
            return {
                label: getRequiredDocumentLabel('Resident Permit'),
                info: 'Document serving as proof of resident permit, including examples like a settlement permit, blue card, or cross-border commuter permit.',
            };
        case 'passport':
            return {
                label: getRequiredDocumentLabel('Passport'),
                info: 'A copy of the passport document containing the main page with picture and personal information and the page with the signature (if not on the main page).',
            };
        case 'invoice-incoming':
            return {
                label: getRequiredDocumentLabel(
                    'Invoice or contract for incoming payments',
                ),
                info: 'Three recent invoices issued to main customers for incoming payments. If available, also contracts or agreements related to these transactions. These documents confirm the nature of incoming payments.',
            };
        case 'invoice-outgoing':
            return {
                label: getRequiredDocumentLabel(
                    'Invoice or contract for outgoing payments',
                ),
                info: 'Three recent invoices received from main suppliers or business partners for outgoing payments. If available, also contracts or agreements related to these transactions. These documents confirm the nature of outgoing payments.',
            };
        case 'bank_account_statement':
            return {
                label: getRequiredDocumentLabel('Bank Account Statement'),
                info: 'A recent bank account statement of the last 3 months showing payments corresponding to the submitted invoices or contracts.',
            };
        case 'financial_statement':
            return {
                label: getRequiredDocumentLabel('Financial Statement'),
                info: 'Most recent / latest financial statement.',
            };
        case 'audit_statement':
            return {
                label: getRequiredDocumentLabel('Audit Statement'),
                info: 'Most recent / latest and signed audit statement.',
            };
        case 'private_source_of_funds':
            return {
                label: getRequiredDocumentLabel('Private source of funds'),
                info: 'Document demonstrating the origin of funds from private sources such as personal savings, inheritance, or the sale of personal assets. Source, name, address, amount, and date/time stamp should be visible on the document.',
            };
        case 'cv':
            return {
                label: getRequiredDocumentLabel('CV or Professional Summary'),
                info: 'CV or a summary of employment / business activities to support the validation of your business model, expertise, and the source of wealth / funds.',
            };
        case 'extract_of_company_register':
            return {
                label: getRequiredDocumentLabel('Company Register Extract'),
                info: 'Official extract of the company register issued by state / government.',
            };
        case 'certificate_of_incorporation':
            return {
                label: getRequiredDocumentLabel('Certificate of Incorporation'),
                info: 'Official extract and / or legalized copies of the documents of incorporation of the company.',
            };
        case 'article_of_association':
            return {
                label: getRequiredDocumentLabel('Articles of Association'),
                info: 'Actual copy of the articles of association including recent board resolutions of the company.',
            };
        case 'beneficial_ownership_register':
            return {
                label: getRequiredDocumentLabel(
                    'Beneficial Ownership Register',
                ),
                info: 'Ultimate beneficial owner and / or shareholder of the company.',
            };
        default:
            return {
                label: requiredDocument.category,
                info: '',
            };
    }
};

const getRequiredInfoText = ({
    requiredInfo,
}: {
    requiredInfo: BusinessPartnerRequiredInformation;
}): {label: React.ReactNode; info: React.ReactNode} => {
    switch (requiredInfo.question) {
        case 'business_model_description':
            return {
                label: 'Business model description',
                info: (
                    <div>
                        <p>
                            Information of how you earn your money and how your
                            business model works.
                        </p>

                        <p>
                            Example: We plan, develop and produce cooling
                            appliances from private use to large industrial
                            plants. The company has a long family tradition and
                            wants to expand into new markets such as DE, FR. In
                            the near future we will also produce electronic
                            components especially for refrigeration appliances
                            and increase our workforce to approx. 40 employees.
                        </p>
                    </div>
                ),
            };
        case 'use_of_amnis_description':
            return {
                label: 'Use of amnis description',
                info: (
                    <div>
                        <p>
                            Explanation of the use of amnis for you / your
                            business.
                        </p>

                        <p>
                            Example: collect customer payments in different
                            currencies; pay supplier in foreign currencies each
                            month; use debit card for each employee to get more
                            efficiency in accounting and treasury
                        </p>
                    </div>
                ),
            };
        case 'social_media_link':
            return {
                label: (
                    <span data-notranslate>
                        {translate('Social media link for %{crpFullName}', {
                            crpFullName: requiredInfo.crpFullName,
                        })}
                    </span>
                ),
                info: (
                    <div>
                        <p>
                            Let us know how you are present on the social media
                            / internet.
                        </p>

                        <p>
                            Please provide the respective link e.g. LinkedIn,
                            Xing, Instagram, Facebook, etc. The information is
                            required to validate the business model including
                            source of wealth / fund.
                        </p>
                    </div>
                ),
            };

        default:
            return {
                label: requiredInfo.question,
                info: '',
            };
    }
};

const getPaymentCustomerForRequiredDocument = (
    requiredDocument: BusinessPartnerRequiredDocument,
    businessPartner: BusinessPartner,
): PaymentCustomer | null => {
    if (
        requiredDocument.category !== 'incoming_payment_customer' &&
        requiredDocument.category !== 'outgoing_payment_customer'
    )
        return null;

    if (requiredDocument.category === 'incoming_payment_customer') {
        return businessPartner.gwgInfo?.incomingPaymentCustomers.find(
            c => c.uniqueId === requiredDocument.uniqueId,
        )!;
    }
    return businessPartner.gwgInfo?.outgoingPaymentCustomers.find(
        c => c.uniqueId === requiredDocument.uniqueId,
    )!;
};

const getCrpBpForRequiredDocument = (
    requiredDocument: BusinessPartnerRequiredDocument,
    crpBpList: ICrpBpListItem[],
) => {
    if (
        (requiredDocument.category !== 'utility_bill' &&
            requiredDocument.category !== 'id_document') ||
        !crpBpList?.length
    )
        return null;

    return crpBpList.find(
        crpBp => +crpBp.crp['@id'].split('/').pop()! === requiredDocument.crpId,
    );
};

const BusinessPartnerDocumentsUpload = (props: IDefaultStepProps) => {
    const {activeBusinessPartner, onSuccess, crpList, legalType} = props;
    const {data} = useGetBusinessPartnerRequiredDocuments(
        activeBusinessPartner,
    );
    const {data: infos} = useGetBusinessPartnerRequiredInformations(
        activeBusinessPartner,
    );
    const {mutate: saveQuestions, isPending: isLoading} =
        useAddBusinessPartnerRequiredInformations();
    const {updateStatus, uploadStatuses, removeStatus} =
        useFileUploadStatuses();
    const {mutate: uploadBpDocument} = useUploadBusinessPartnerDocument();
    const {mutate: uploadCrpDocument} = useUploadCrpDocument();
    const queryClient = useQueryClient();
    const formMethods = useForm<
        Record<BusinessPartnerRequiredInformation['question'], string>
    >({
        defaultValues: {
            business_model_description: '',
            social_media_link: '',
            use_of_amnis_description: '',
        },
    });

    useEffect(() => {
        if (infos?.length) {
            formMethods.reset({
                business_model_description:
                    infos.find(
                        info => info.question === 'business_model_description',
                    )?.answer || '',
                social_media_link:
                    infos.find(info => info.question === 'social_media_link')
                        ?.answer || '',
                use_of_amnis_description:
                    infos.find(
                        info => info.question === 'use_of_amnis_description',
                    )?.answer || '',
            });
        }
    }, [infos]);

    const handleFileUpload = (
        files: FileList | null,
        requiredDocument: BusinessPartnerRequiredDocument,
    ) => {
        if (!files) return;
        const file = files[0];
        if (
            !uploadStatuses.find(
                f =>
                    f.name === file.name &&
                    isNewFileUploadStatus(f) &&
                    f.document === file,
            )
        ) {
            const callbacks = {
                onSuccess: () => {
                    removeStatus(file);
                    queryClient.invalidateQueries({
                        queryKey: [REQUIRED_DOCUMENTS],
                    });
                },
                onError: (errorResponse: any) => {
                    updateStatus({
                        document: file,
                        status: 'error',
                        name: file.name,
                        category: requiredDocument.category,
                        errorMessage:
                            errorResponse['hydra:description'] ||
                            'Something went wrong',
                    });
                },
            };

            updateStatus({
                document: file,
                status: 'loading',
                name: file.name,
            });
            if (requiredDocument.crpId) {
                const crpBp = crpList?.find(
                    c =>
                        +c.crp['@id'].split('/').pop()! ===
                        requiredDocument.crpId,
                );

                if (!crpBp) {
                    throw new Error(
                        `CRP with id ${requiredDocument.crpId} not found`,
                    );
                }

                uploadCrpDocument(
                    {
                        description: '', // TODO
                        files: Array.from(files),
                        ...requiredDocument,
                    },
                    callbacks,
                );
                return;
            }
            uploadBpDocument(
                {
                    activeBpId: activeBusinessPartner['@id'],
                    files: Array.from(files),
                    type: 'onboarding',
                    ...requiredDocument,
                },
                callbacks,
            );
        }
    };

    const handleSubmit: FormOnSubmit<
        Record<BusinessPartnerRequiredInformation['question'], string>
    > = (values, setError) => {
        if (!infos?.length) return onSuccess();

        const infosWithAnswers = infos.map(info => ({
            ...info,
            answer: values[info.question],
        }));

        saveQuestions(
            {businessPartner: activeBusinessPartner, infos: infosWithAnswers},
            {
                onSuccess: () => onSuccess(),
                onError: (err: any) =>
                    handleTypedFormError(err, setError, values),
            },
        );
    };

    return (
        <div className={styles.form}>
            <StepTitle title="Required documents" />

            {!data?.length ? (
                <div>
                    <p>All required documents are uploaded</p>
                </div>
            ) : (
                <>
                    <p>
                        Please upload the requested files (.pdf/.png/.jpg) in
                        their designated fields.
                    </p>
                    <Form onSubmit={() => false}>
                        <div
                            className={classNames(
                                styles.documentRow,
                                styles.first,
                            )}
                        >
                            <p data-notranslate>
                                {translate('%{count} requested file(s)', {
                                    count: data?.length,
                                })}
                            </p>
                            <p>File status</p>
                        </div>
                        {data?.map(requiredDocument => {
                            const paymentCustomer =
                                getPaymentCustomerForRequiredDocument(
                                    requiredDocument,
                                    activeBusinessPartner,
                                );

                            const crpBp = getCrpBpForRequiredDocument(
                                requiredDocument,
                                crpList,
                            );

                            return (
                                <div
                                    className={styles.documentRow}
                                    key={JSON.stringify(requiredDocument)}
                                >
                                    <div className={styles.documentInfo}>
                                        <span
                                            className={styles.documentLabel}
                                            data-notranslate
                                        >
                                            {
                                                getRequiredDocumentText({
                                                    requiredDocument,
                                                    legalType,
                                                    paymentCustomer,
                                                    businessPartner:
                                                        activeBusinessPartner,
                                                })?.label
                                            }
                                            <InfoPopover
                                                content={
                                                    getRequiredDocumentText({
                                                        requiredDocument,
                                                        legalType,
                                                        paymentCustomer,
                                                        businessPartner:
                                                            activeBusinessPartner,
                                                    })?.info
                                                }
                                            />
                                        </span>
                                        {requiredDocument.documentFileName ? (
                                            <span
                                                className={
                                                    styles.currentDocument
                                                }
                                            >
                                                {
                                                    requiredDocument.documentFileName
                                                }
                                                <IconSVG name="checkMark" />
                                            </span>
                                        ) : null}
                                    </div>
                                    <FileUploadButton
                                        name={JSON.stringify(requiredDocument)}
                                        containerClassname={styles.inputField}
                                        hideHelperText
                                        uploadStatuses={uploadStatuses}
                                        updateStatus={updateStatus}
                                        removeStatus={removeStatus}
                                        onChange={e =>
                                            handleFileUpload(
                                                e.target.files,
                                                requiredDocument,
                                            )
                                        }
                                    />
                                </div>
                            );
                        })}
                    </Form>
                </>
            )}
            <Form formMethods={formMethods} onSubmit={handleSubmit}>
                {!infos?.length ? (
                    <div>
                        <p>All required informations are submitted</p>
                    </div>
                ) : (
                    <>
                        {infos.map(info => (
                            <TextArea
                                name={info.question}
                                label={
                                    getRequiredInfoText({requiredInfo: info})
                                        .label
                                }
                                info={
                                    getRequiredInfoText({requiredInfo: info})
                                        .info
                                }
                                variant="dark"
                                size="large"
                            />
                        ))}
                    </>
                )}
                <NextStepButton
                    loading={isLoading}
                    disabled={uploadStatuses.some(
                        st => st.status !== 'uploaded',
                    )}
                />
            </Form>
        </div>
    );
};

export default BusinessPartnerDocumentsUpload;
